import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AddonModal from './AddonModal';
import { FetchAddons, DeleteAddon, addonsSelector, totalCountSelector, loadingSelector } from './SubscriptionSlice';

const AddonsList = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const addons = useSelector(addonsSelector);
  const total = useSelector(totalCountSelector);
  const loading = useSelector(loadingSelector);

  useEffect(() => {
    loadAddons();
  }, [currentPage, searchTerm]);

  const loadAddons = () => {
    dispatch(FetchAddons({ page: currentPage, limit, search: searchTerm }));
  };

  const handleCreate = () => {
    setSelectedAddon(null);
    setShowModal(true);
  };

  const handleEdit = (addon) => {
    setSelectedAddon(addon);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this add-on?')) {
      const result = await dispatch(DeleteAddon(id));
      if (result.payload?.code === 1) {
        toast.success('Add-on deleted successfully');
        loadAddons();
      } else {
        toast.error('Failed to delete add-on');
      }
    }
  };

  const handleModalClose = (refresh) => {
    setShowModal(false);
    setSelectedAddon(null);
    if (refresh) {
      loadAddons();
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="row align-items-center">
        <div className="col-sm-12 col-md-6 col-lg-6"></div>
        <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
          <div className="d-flex align-items-center lg-justify-content-end mb-3">
            <form className="has-search">
              <span className="form-search-icon form-control-feedback"></span>
              <input
                type="text"
                className="form-control shadow"
                placeholder="Search add-ons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </form>
            <span onClick={handleCreate} className="" style={{ cursor: 'pointer' }}>
              <img
                src="assets/images/addBtn.png"
                alt=""
                className="add-new"
              />
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="table-responsive text-center">
            <table className="table storetable">
              <thead className="text-center">
                <tr>
                  <th scope="col">Add-On Name</th>
                  <th scope="col">Description</th>
                  <th scope="col">Price</th>
                  <th scope="col">Stripe Price ID</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
                <tr className="border-0 bg-none invisible">
                  <td className="border-0 bg-none"></td>
                </tr>
              </thead>
              <tbody>
                {addons && addons.length > 0 ? (
                  addons.map((addon) => (
                    <tr key={addon.id}>
                      <td>{addon.addon_name}</td>
                      <td>{addon.description || '-'}</td>
                      <td>£{parseFloat(addon.price).toFixed(2)}</td>
                      <td><small>{addon.stripe_price_id || '-'}</small></td>
                      <td>
                        <span
                          className={`badge ${
                            addon.is_active ? 'badge-success' : 'badge-secondary'
                          }`}
                        >
                          {addon.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex justify-content-center action align-items-center">
                          <button
                            className="btn"
                            onClick={() => handleEdit(addon)}
                            title="Edit Add-On"
                          >
                            <img
                              src="assets/images/edit.png"
                              alt=""
                            />
                          </button>
                          <button
                            className="btn"
                            onClick={() => handleDelete(addon.id)}
                            title="Delete Add-On"
                          >
                            <img
                              src="assets/images/delete.png"
                              alt=""
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No add-ons found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, index) => (
                    <li
                      key={index}
                      className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {showModal && (
        <AddonModal
          show={showModal}
          onClose={handleModalClose}
          addon={selectedAddon}
        />
      )}
    </div>
  );
};

export default AddonsList;
